<template>
  <div style="width: 100%">
    <v-row class="pa-0 ma-0  overflow-x-hidden" style="flex-wrap: nowrap">
      <v-tooltip v-for="(segment, i) in segments"
                 v-bind:key="i"
                 bottom
                 class="ma-0"
                 :disabled="isPlaceholder(segment)"
      >
        <template v-slot:activator="{ on }">
          <v-card :width="segment.widthPercentage + '%'"
                  :ref="timePairId + '_expected_' + i"
                  :style="{
                    'border-radius': getSegmentBorderRadius(i, segments.length),
                    'background': getSegmentBackground(segment.category),
                    'border': getSegmentBorder(segment.category)
                  }"
                  height="20px"
                  v-on="on"
                  flat
                  tile
          >
            <v-container fluid class="pa-0 fill-height">
              <v-row v-if="!isPlaceholder(segment) && !isFlextime(segment)"
                     class="pa-0 ma-0"
                     align="center"
                     justify="end"
              >
                <v-col class="pa-0">
                  <div class="caption text-truncate text-center font-weight-regular mx-2"
                       :style="{ color: getSegmentTextColour(segment.category) }"
                  >
                    {{ getReadableCategory(segment.category) }}
                  </div>
                </v-col>
              </v-row>
              <v-row v-if="isFlextime(segment)" align="center" class="ml-0">
                <div class="flexTimeVertical"/>
                <div class="flexTimeHorizontal"/>
                <div class="flexTimeVertical"/>
              </v-row>
            </v-container>
          </v-card>
        </template>
        <v-container>
          <v-layout>
            <v-col>
              <v-row>
                {{`${$t('workSchedules.startTime')}: ` + segment.startTime }}
              </v-row>
              <v-row>
                {{ `${$t('workSchedules.endTime')}: ` + segment.endTime }}
              </v-row>
              <v-row>
                {{ `${$t('tableHeaders.duration')}: ` + formatDuration(segment.minuteDuration) }}
              </v-row>
              <v-row>
                {{ `${$t('misc.type')}: ` + getReadableCategory(segment.category) }}
              </v-row>
            </v-col>
          </v-layout>
        </v-container>
      </v-tooltip>
    </v-row>
  </div>
</template>

<script>
import constants from '../../constants';
import DateTimeUtil from '../../util/date-time-util';

export default {
  name: 'WorkScheduleBar',
  props: {
    segments: {
      type: Array,
      required: true,
    },
    timePairId: {
      type: Number,
      required: true,
    },
    spanningDays: {
      type: Number,
      required: true,
    },
    faded: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  methods: {
    isPlaceholder(segment) {
      return segment.category === constants.WorkSchedule.SegmentType.PLACEHOLDER;
    },
    isFlextime(segment) {
      return segment.category === constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH;
    },
    getSegmentBorderRadius(index, numTimeSegments) {
      if (numTimeSegments === 1) {
        return '3px 3px 3px 3px';
      }
      if (index === 0) {
        return '3px 0px 0px 3px';
      }
      if (index === numTimeSegments - 1) {
        return '0px 3px 3px 0px';
      }
      return '0px';
    },
    getSegmentBackground(segmentCategory) {
      let background;
      switch (segmentCategory) {
        case constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH:
          background = '#f5f5f5';
          break;
        case constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME:
          background = 'var(--v-primary-lighten2)';
          break;
        case constants.WorkSchedule.SegmentType.SCHEDULED_UNPAID_BREAK:
          background = 'var(--v-accent-lighten1)';
          break;
        case constants.WorkSchedule.SegmentType.ACTUAL_WORKING_TIME:
          background = 'var(--v-success-lighten1)';
          break;
        case constants.WorkSchedule.SegmentType.ONGOING_WORK_TIME:
          background = `repeating-linear-gradient(
            135deg,
            var(--v-success-lighten1),
            var(--v-success-lighten1) 15px,
            var(--v-success-lighten2) 15px,
            var(--v-success-lighten2) 30px
          )`;
          break;
        case constants.WorkSchedule.SegmentType.ACTUAL_BREAK_TIME:
        case constants.WorkSchedule.SegmentType.OT_BREAK:
          background = 'var(--v-info-darken2)';
          break;
        case constants.WorkSchedule.SegmentType.ONGOING_BREAK_TIME:
          background = `repeating-linear-gradient(
            135deg,
            var(--v-info-darken2),
            var(--v-info-darken2) 15px,
            var(--v-info-darken1) 15px,
            var(--v-info-darken1) 30px
          )`;
          break;
        case constants.WorkSchedule.SegmentType.MISSED_WORK:
          background = 'var(--v-warning-base)';
          break;
        case constants.WorkSchedule.SegmentType.OVERTIME:
          background = 'var(--v-primary-lighten3)';
          break;

        case constants.WorkSchedule.SegmentType.ONGOING_OVERTIME:
          background = 'var(--v-primary-lighten4)';
          break;

        case constants.WorkSchedule.SegmentType.FIXED_OVERTIME:
          background = 'var(--v-primary-darken1)';
          break;
        case constants.WorkSchedule.SegmentType.FIXED_OVERTIME_WORKING_TIME:
          background = 'var(--v-primary-lighten1)';
          break;
        case constants.WorkSchedule.SegmentType.EARLY_LOGIN:
          background = 'var(--v-success-darken1)';
          break;
        case constants.WorkSchedule.SegmentType.PRE_SHIFT_OT:
          background = 'var(--v-success-darken2)';
          break;
        default:
          background = '#f5f5f5';
          break;
      }
      return background;
    },
    getSegmentBorder(segmentCategory) {
      let border;
      switch (segmentCategory) {
        case constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH:
        case constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME:
          border = '0px solid var(--v-primary-lighten3)';
          break;
        case constants.WorkSchedule.SegmentType.SCHEDULED_UNPAID_BREAK:
          border = '0px solid var(--v-secondary-base)';
          break;
        default:
          border = 'none';
          break;
      }
      return border;
    },
    formatDuration(durationInMinutes) {
      return DateTimeUtil.getFormattedDuration(durationInMinutes);
    },
    getReadableCategory(segmentCategory) {
      let category;
      switch (segmentCategory) {
        case constants.WorkSchedule.SegmentType.FLEXTIME_BANDWIDTH:
          category = this.$t('workSchedules.felxtimeWindow');
          break;
        case constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME:
          category = this.$t('workSchedules.scheduledTime');
          break;
        case constants.WorkSchedule.SegmentType.SCHEDULED_UNPAID_BREAK:
          category = this.$t('workSchedules.scheduledBreak');
          break;
        case constants.WorkSchedule.SegmentType.ACTUAL_WORKING_TIME:
          category = this.$t('workSchedules.workingTime');
          break;
        case constants.WorkSchedule.SegmentType.ONGOING_WORK_TIME:
          category = this.$t('workSchedules.stillWorking');
          break;
        case constants.WorkSchedule.SegmentType.ACTUAL_BREAK_TIME:
          category = this.$tc('misc.break', 1);
          break;
        case constants.WorkSchedule.SegmentType.ONGOING_BREAK_TIME:
          category = this.$t('workSchedules.ongoingBreak');
          break;
        case constants.WorkSchedule.SegmentType.MISSED_WORK:
        case constants.WorkSchedule.SegmentType.OVERTIME:
          category = this.$t('workSchedules.overtime');
          break;
        case constants.WorkSchedule.SegmentType.ONGOING_OVERTIME:
          category = 'Ongoing Overtime';
          break;
        case constants.WorkSchedule.SegmentType.FIXED_OVERTIME:
          category = 'Scheduled Overtime';
          break;
        case constants.WorkSchedule.SegmentType.FIXED_OVERTIME_WORKING_TIME:
          category = 'Scheduled Overtime Working Time';
          break;
        case constants.WorkSchedule.SegmentType.EARLY_LOGIN:
          category = 'Early Login';
          break;
        case constants.WorkSchedule.SegmentType.PRE_SHIFT_OT:
          category = 'Pre-Shift Overtime';
          break;
        case constants.WorkSchedule.SegmentType.OT_BREAK:
          category = 'Overtime Break';
          break;
        default:
          category = this.$t('misc.unknown');
          break;
      }
      return category;
    },
    getSegmentTextColour(segmentCategory) {
      let colour;
      switch (segmentCategory) {
        case constants.WorkSchedule.SegmentType.SCHEDULED_WORKING_TIME:
        case constants.WorkSchedule.SegmentType.FIXED_OVERTIME:
        case constants.WorkSchedule.SegmentType.FIXED_OVERTIME_WORKING_TIME:
        case constants.WorkSchedule.SegmentType.EARLY_LOGIN:
          colour = 'white';
          break;
        default:
          colour = '#232323';
          break;
      }
      return colour;
    },
  },
};
</script>

<style scoped>
.flexTimeVertical {
  background-color: var(--v-primary-lighten3);
  height:20px;
  width:4px;
}

.flexTimeHorizontal {
  background: repeating-linear-gradient(
    90deg,
    var(--v-primary-lighten2),
    var(--v-primary-lighten2) 15px,
    var(--v-primary-lighten4) 15px,
    var(--v-primary-lighten4) 30px
  );
  height:6px;
  width:calc(100% - 8px);
}
</style>
